(() => {
  'use strict';
  const DAY = 86400000;
  const MIN_YEAR = 2026;
  const MAX_YEAR = 2030;
  const defaults = Object.freeze({ enabled: true, min: 1, max: 3, deliveryDays: 'mon-sat', shippingDays: 'mon-fri', cutoff: '16:00', closures: '' });
  const holidayDefaults = Object.freeze(Object.fromEntries(['new_year','epiphany','good_friday','easter_monday','labour_day','ascension','whit_monday','corpus_christi','german_unity','all_saints','christmas','boxing_day'].map(name => [name, true])));
  const calendars = new Map();
  const key = date => date.toISOString().slice(0, 10);
  const add = (date, days) => new Date(date.getTime() + days * DAY);
  const civil = (year, month, day) => new Date(Date.UTC(year, month - 1, day));
  const supported = date => date.getUTCFullYear() >= MIN_YEAR && date.getUTCFullYear() <= MAX_YEAR;

  function configuration(input = {}, country = 'DE') {
    if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
    const value = { ...defaults, ...input };
    if (typeof value.enabled !== 'boolean' || !Number.isInteger(value.min) || !Number.isInteger(value.max) || value.min < 1 || value.min > (country === 'DE' ? 10 : 365) || value.max < value.min || value.max > (country === 'DE' ? 20 : 365)) return null;
    if (value.holidays !== undefined && (!value.holidays || typeof value.holidays !== 'object' || Array.isArray(value.holidays))) return null;
    const holidays = { ...holidayDefaults, ...value.holidays };
    if (Object.values(holidays).some(enabled => typeof enabled !== 'boolean')) return null;
    if (!['mon-fri', 'mon-sat'].includes(value.shippingDays) || !['mon-fri', 'mon-sat'].includes(value.deliveryDays)) return null;
    if (typeof value.cutoff !== 'string' || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value.cutoff) || typeof value.closures !== 'string') return null;
    const closed = new Set();
    for (const line of value.closures.split(/\r?\n/)) {
      const date = line.trim();
      if (!date) continue;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
      const parsed = new Date(date + 'T00:00:00Z');
      if (!Number.isFinite(parsed.getTime()) || key(parsed) !== date || !supported(parsed)) return null;
      closed.add(date);
    }
    const [hour, minute] = value.cutoff.split(':').map(Number);
    return { ...value, holidays, closed, cutoffMinutes: hour * 60 + minute };
  }

  const workday = (date, days) => date.getUTCDay() > 0 && date.getUTCDay() < (days === 'mon-sat' ? 7 : 6);

  function easter(year) {
    const a = year % 19, b = Math.floor(year / 100), c = year % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451), n = h + l - 7 * m + 114;
    return civil(year, Math.floor(n / 31), n % 31 + 1);
  }

  function calendar(year) {
    if (calendars.has(year)) return calendars.get(year);
    const fixed = dates => dates.map(([month, day]) => key(civil(year, month, day)));
    const sunday = easter(year);
    const national = new Set([
      ...fixed([[1, 1], [5, 1], [10, 3], [12, 25], [12, 26]]),
      ...[-2, 1, 39, 50].map(offset => key(add(sunday, offset)))
    ]);
    // Oberasbach: Epiphany, Corpus Christi, All Saints; no Assumption/Augsburg festival.
    const warehouse = new Set([...national, ...fixed([[1, 6], [11, 1]]), key(add(sunday, 60))]);
    const result = { national, warehouse };
    calendars.set(year, result);
    return result;
  }

  function warehouseDates(year) {
    const sunday = easter(year);
    return {
      new_year: key(civil(year, 1, 1)), epiphany: key(civil(year, 1, 6)),
      good_friday: key(add(sunday, -2)), easter_monday: key(add(sunday, 1)),
      labour_day: key(civil(year, 5, 1)), ascension: key(add(sunday, 39)),
      whit_monday: key(add(sunday, 50)), corpus_christi: key(add(sunday, 60)),
      german_unity: key(civil(year, 10, 3)), all_saints: key(civil(year, 11, 1)),
      christmas: key(civil(year, 12, 25)), boxing_day: key(civil(year, 12, 26))
    };
  }

  function berlinClock(now) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
    return { date: civil(+values.year, +values.month, +values.day), minutes: +values.hour * 60 + +values.minute };
  }

  function estimate(now, country = 'DE', input = {}) {
    if (country !== 'DE') return { kind: 'fallback', reason: 'country' };
    if (input?.enabled === false) return { kind: 'fallback', reason: 'disabled' };
    const config = configuration(input);
    if (!config) return { kind: 'fallback', reason: 'configuration' };
    if (!now || !Number.isFinite(now.getTime?.())) return { kind: 'fallback', reason: 'clock' };
    const clock = berlinClock(now);
    if (!supported(clock.date)) return { kind: 'fallback', reason: 'calendar' };
    let dispatch = add(clock.date, clock.minutes >= config.cutoffMinutes ? 1 : 0);
    if (!supported(dispatch)) return { kind: 'fallback', reason: 'calendar' };
    let guard = 0;
    while (!workday(dispatch, config.shippingDays) || Object.entries(warehouseDates(dispatch.getUTCFullYear())).some(([name,date]) => config.holidays[name] && date === key(dispatch)) || config.closed.has(key(dispatch))) {
      dispatch = add(dispatch, 1);
      if (!supported(dispatch) || ++guard > 366) return { kind: 'fallback', reason: 'calendar' };
    }
    const arrivals = [];
    let day = dispatch;
    while (arrivals.length < config.max) {
      day = add(day, 1);
      if (!supported(day) || ++guard > 500) return { kind: 'fallback', reason: 'calendar' };
      const holidays = calendar(day.getUTCFullYear());
      if (!workday(day, config.deliveryDays) || holidays.national.has(key(day))) continue;
      arrivals.push(key(day));
    }
    return { kind: 'dates', dispatch: key(dispatch), first: arrivals[config.min - 1], last: arrivals[config.max - 1] };
  }

  function formatWindow(result, locale = 'de') {
    const first = new Date(result.first + 'T00:00:00Z');
    const last = new Date(result.last + 'T00:00:00Z');
    const sameYear = first.getUTCFullYear() === last.getUTCFullYear();
    const format = new Intl.DateTimeFormat(locale, {
      timeZone: 'UTC', day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' })
    });
    if (format.formatRange) return format.formatRange(first, last);
    return format.format(first) + ' – ' + format.format(last);
  }

  function estimateBounds(bounds, country = 'DE', input = {}) {
    if (!bounds || !Number.isFinite(bounds.first) || !Number.isFinite(bounds.last) || bounds.last < bounds.first) return { kind: 'fallback', reason: 'clock' };
    const first = estimate(new Date(bounds.first), country, input);
    const last = estimate(new Date(bounds.last), country, input);
    return JSON.stringify(first) === JSON.stringify(last) ? first : { kind: 'fallback', reason: 'clock' };
  }

  function createTrustedClock(environment) {
    const listeners = new Set();
    const mono = environment.monotonic;
    let anchor = null, pending = null, generation = 0, retryAt = 0, queued = false;
    let timer, lifecycle, lastTick, lastWall, lastWake = -Infinity, suspended = false;
    const notify = () => { for (const listener of [...listeners]) listener(); };
    const visible = () => !suspended && !environment.document?.hidden;
    const schedule = () => {
      if (queued || pending || !listeners.size || !visible() || mono() < retryAt) return;
      if (anchor && mono() >= anchor.received && mono() - anchor.received < 45000) return;
      queued = true;
      environment.microtask(() => { queued = false; void refresh(); });
    };
    const invalidate = (resync = true) => {
      anchor = null;
      generation++;
      pending?.abort.abort();
      pending = null;
      if (resync) schedule();
    };
    const guard = () => {
      const current = mono?.(), wall = environment.wall?.();
      if (!Number.isFinite(current) || !Number.isFinite(wall)) { invalidate(false); return null; }
      if (lastTick !== undefined && (current < lastTick || current - lastTick > 10000 || Math.abs((wall - lastWall) - (current - lastTick)) > 2000)) invalidate();
      lastTick = current;
      lastWall = wall;
      return current;
    };
    const read = () => {
      const current = guard();
      if (!visible() || current === null) return null;
      if (!anchor) { schedule(); return null; }
      const elapsed = current - anchor.received;
      if (elapsed < 0 || elapsed >= 60000) { anchor = null; schedule(); return null; }
      if (elapsed >= 45000) schedule();
      return { first: anchor.first + elapsed, last: anchor.last + elapsed };
    };
    async function refresh() {
      if (pending) return pending.promise;
      const start = guard();
      if (!listeners.size || !visible() || start === null || start < retryAt) return;
      const job = { generation, abort: new AbortController() };
      pending = job;
      job.promise = (async () => {
        let timeout;
        try {
          const url = new URL(environment.root() + 'cart.js', environment.origin);
          if (url.origin !== environment.origin) throw new Error('origin');
          url.searchParams.set('_delivery_clock', environment.nonce());
          const response = await Promise.race([
            environment.fetch(url.href, { method: 'GET', credentials: 'same-origin', mode: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: job.abort.signal }),
            new Promise((_, reject) => { timeout = environment.setTimeout(() => { job.abort.abort(); reject(new Error('timeout')); }, 4000); })
          ]);
          const received = mono();
          const actualURL = new URL(response.url);
          const date = response.headers.get('Date');
          const parsed = Date.parse(date);
          const age = response.headers.get('Age');
          const cache = response.headers.get('CF-Cache-Status');
          const type = response.headers.get('Content-Type') || '';
          response.body?.cancel?.().catch?.(() => {});
          if (response.status !== 200 || actualURL.origin !== url.origin || actualURL.pathname !== url.pathname || !/^(application\/json|text\/javascript)(?:;|$)/i.test(type)) throw new Error('response');
          if (!date || !Number.isFinite(parsed) || new Date(parsed).toUTCString() !== date || (age !== null && !/^0+$/.test(age)) || (cache && !['DYNAMIC', 'BYPASS', 'MISS'].includes(cache.toUpperCase()))) throw new Error('freshness');
          if (!Number.isFinite(received) || received < start || received - start > 4000) throw new Error('duration');
          if (job.generation !== generation || !visible() || !listeners.size) return;
          anchor = { first: parsed, last: parsed + 1000 + received - start, received };
          retryAt = 0;
        } catch (_) {
          if (job.generation !== generation) return;
          anchor = null;
          retryAt = (mono?.() || 0) + 30000;
        } finally {
          environment.clearTimeout(timeout);
          if (pending === job) { pending = null; notify(); }
        }
      })();
      return job.promise;
    }
    const wake = () => {
      const current = mono?.();
      if (Number.isFinite(current) && current - lastWake < 250) { schedule(); return; }
      lastWake = current;
      invalidate();
      guard();
      notify();
    };
    const subscribe = listener => {
      listeners.add(listener);
      if (listeners.size === 1) {
        lifecycle = new AbortController();
        const options = { signal: lifecycle.signal };
        environment.document?.addEventListener?.('visibilitychange', () => {
          if (environment.document.hidden) { invalidate(false); notify(); }
          else wake();
        }, options);
        environment.window?.addEventListener?.('pagehide', () => { suspended = true; invalidate(false); notify(); }, options);
        environment.window?.addEventListener?.('pageshow', () => { suspended = false; wake(); }, options);
        environment.window?.addEventListener?.('focus', wake, options);
        timer = environment.setInterval(() => { const before = anchor; read(); if (before && !anchor) notify(); }, 1000);
        schedule();
      }
      return () => {
        listeners.delete(listener);
        if (!listeners.size) { environment.clearInterval(timer); lifecycle?.abort(); invalidate(false); }
      };
    };
    return { read, refresh, subscribe, invalidate };
  }

  const api = { estimate, estimateBounds, createTrustedClock, formatWindow, berlinClock, calendar, configuration, warehouseDates, minYear: MIN_YEAR, maxYear: MAX_YEAR };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined') return;
  window.DorigineDelivery = api;
  if (customElements.get('dorigine-delivery')) return;
  api.clock = createTrustedClock({
    monotonic: () => typeof performance !== 'undefined' ? performance.now() : NaN,
    wall: () => Date.now(),
    root: () => window.Shopify?.routes?.root || '/',
    origin: window.location?.origin,
    nonce: () => crypto.randomUUID(),
    fetch: (...args) => window.fetch(...args),
    microtask: callback => window.queueMicrotask(callback),
    setTimeout: (callback, delay) => window.setTimeout(callback, delay),
    clearTimeout: timer => window.clearTimeout(timer),
    setInterval: (callback, delay) => window.setInterval(callback, delay),
    clearInterval: timer => window.clearInterval(timer),
    document, window
  });

  class DorigineDelivery extends HTMLElement {
    connectedCallback() {
      this.windowNode = this.querySelector('[data-delivery-window]');
      this.suffixNode = this.querySelector('[data-delivery-suffix]');
      this.fallbackText ??= this.windowNode.textContent;
      this.button = this.querySelector('button');
      this.infoPanel = this.querySelector('.dorigine-delivery-popover');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.button?.addEventListener('click', () => this.toggleInfo(this.infoPanel.hidden), options);
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) this.toggleInfo(false);
        if (this.closest('[data-hstaffel]')?.contains(event.target)) queueMicrotask(() => this.update());
      }, options);
      document.addEventListener('change', event => {
        if (this.closest('[data-hstaffel]')?.contains(event.target)) queueMicrotask(() => this.update());
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.infoPanel && !this.infoPanel.hidden) { this.toggleInfo(false); this.button.focus(); }
      }, options);
      this.connected = true;
      this.update();
      this.timer = setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.abort?.abort();
      this.connected = false;
      this.clockUnsubscribe?.();
      this.clockUnsubscribe = null;
    }

    toggleInfo(open) {
      if (!this.infoPanel || !this.button) return;
      this.infoPanel.hidden = !open;
      this.button.setAttribute('aria-expanded', String(open));
    }

    update() {
      try {
        const country = document.documentElement.dataset.country_code || this.dataset.country;
        this.hidden = country !== this.dataset.country || !this.staffelStockConfirmed();
        const input = this.dataset.enabled === 'false' ? { enabled: false } : JSON.parse(this.dataset.config);
        const config = configuration(input, this.dataset.country);
        const needsClock = !this.hidden && country === 'DE' && config?.enabled;
        if (needsClock && this.connected) this.clockUnsubscribe ??= api.clock.subscribe(() => this.update());
        if (!needsClock) { this.clockUnsubscribe?.(); this.clockUnsubscribe = null; }
        const result = needsClock ? estimateBounds(api.clock.read(), country, input) : { kind: 'fallback', reason: config ? 'disabled' : 'configuration' };
        if (this.button) this.button.hidden = !config?.enabled;
        const dispatchInfo = this.querySelector('[data-delivery-dispatch]');
        if (dispatchInfo && config) {
          const time = this.dataset.locale?.startsWith('de') && config.cutoff.endsWith(':00') ? String(parseInt(config.cutoff, 10)) : config.cutoff;
          dispatchInfo.textContent = dispatchInfo.dataset.dispatchTemplate.replace('__TIME__', time);
        }
        const signature = JSON.stringify({ result, min: config?.min, max: config?.max });
        if (this.signature === signature) return;
        const fallback = config?.enabled ? this.dataset.fallbackTemplate.replace('__MIN__', config.min).replace('__MAX__', config.max) : this.fallbackText;
        const text = result.kind === 'dates' ? formatWindow(result, this.dataset.locale) : fallback;
        this.windowNode.textContent = text;
        this.suffixNode.textContent = result.kind === 'dates' ? this.dataset.dateSuffix : this.dataset.fallbackSuffix;
        this.signature = signature;
      } catch (_) {
        this.clockUnsubscribe?.();
        this.clockUnsubscribe = null;
        this.signature = undefined;
        if (this.button) this.button.hidden = true;
        this.toggleInfo(false);
        this.windowNode.textContent = this.fallbackText;
        this.suffixNode.textContent = this.dataset.fallbackSuffix;
      }
    }

    staffelStockConfirmed() {
      const staffel = this.closest('[data-hstaffel]');
      if (!staffel) return true;
      const count = +(staffel.querySelector('[data-hstaffel-tile].is-on')?.dataset.hstaffelTile || 1);
      if (count === 1) return true;
      const quantities = new Map();
      for (const select of staffel.querySelectorAll('[data-hstaffel-slot]')) {
        if (+select.dataset.hstaffelSlot > count) continue;
        if (!select.value || select.selectedOptions[0]?.disabled) return false;
        quantities.set(select.value, (quantities.get(select.value) || 0) + 1);
      }
      const inventory = this.closest('product-inventory');
      for (const [id, quantity] of quantities) {
        const markup = inventory.inventories?.[id] || '';
        const stock = markup.match(/data-stock-quantity="(\d+)"/);
        if (!stock || +stock[1] < quantity) return false;
      }
      return Array.from(quantities.values()).reduce((sum, quantity) => sum + quantity, 0) === count;
    }
  }
  customElements.define('dorigine-delivery', DorigineDelivery);
})();
